import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { AlertCircle, Clock, CheckCircle, TrendingUp, FileText, Eye, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "../../components/ui/alert";
import API_CONSTRUCTOR from "../../models/api-model";
import { formatDate, formatStatus, getStatusColor } from "../../utils/adminUtils";
import { getPropertyField } from "../../utils/propertyDataHelper";

/**
 * Admin Dashboard Page
 * Shows statistics and recent queries
 */
export const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentQueries, setRecentQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Initialize API
  const api = API_CONSTRUCTOR();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load statistics
      const statsResponse = await api.get("/queries/statistics");
      setStats(statsResponse.data);

      // Load recent queries
      const queriesResponse = await api.get("/queries?limit=10&sortBy=created_at&sortOrder=DESC");
      setRecentQueries(queriesResponse.data || []);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError(err.response?.data?.error || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="space-y-6">
        {/* Page Header with Refresh */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
            <p className="text-muted-foreground">Overview of flip requests and activity</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadDashboardData}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
        
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">Overview of flip requests and activity</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={loadDashboardData}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Initial Calc</CardTitle>
            <FileText className="h-4 w-4 text-cyan-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending_initial_calculation || 0}</div>
            <p className="text-xs text-muted-foreground">Need initial calculation</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Initial Review</CardTitle>
            <Clock className="h-4 w-4 text-sky-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending_initial_approval || 0}</div>
            <p className="text-xs text-muted-foreground">Homeowner reviewing</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payment</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending_inspection_payment || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting inspection fee</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Detailed Calc</CardTitle>
            <FileText className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending_agent || 0}</div>
            <p className="text-xs text-muted-foreground">Need detailed calc</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Owner Approval</CardTitle>
            <CheckCircle className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending_owner_approval || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.active || 0}</div>
            <p className="text-xs text-muted-foreground">Active deals</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        <Button onClick={() => navigate("/admin/queries")}>View All Queries</Button>
        <Button
          variant="outline"
          onClick={() => navigate("/admin/queries?status=pending_owner_approval")}
        >
          Pending Approvals
        </Button>
      </div>

      {/* Recent Queries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Queries</CardTitle>
        </CardHeader>
        <CardContent>
          {recentQueries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No queries found. New flip requests will appear here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property Address</TableHead>
                  <TableHead>Homeowner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Inspection</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentQueries.map((query) => (
                  <TableRow key={query.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-medium">
                      {query.property_address || getPropertyField(query, 'address', 'N/A')}
                      <div className="text-xs text-muted-foreground">
                        {query.property_city || getPropertyField(query, 'city')}, {query.property_state || getPropertyField(query, 'state')}
                      </div>
                    </TableCell>
                    <TableCell>
                      {query.homeowner_name}
                      <div className="text-xs text-muted-foreground">{query.homeowner_email}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(query.status)} variant="secondary">
                        {formatStatus(query.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {query.inspection_fee_paid ? (
                        <Badge className="bg-green-100 text-green-800" variant="secondary">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Paid
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800" variant="secondary">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(query.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/queries/${query.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;

